// @flow

import { createAction } from 'redux-actions';
import invariant from 'invariant';

import type { ActionType, Thunk } from 'types/redux';
import {
  socialLoginWs,
  type SocialLoginResponse
} from 'api/xws/login/socialLoginWs';

import qs from 'store/entities/QueryString';
import OAuth from 'utils/oauth/OAuth';
import ClientError from 'utils/errors/ClientError';
import { CHANNEL } from 'constants/appConfig';
import { APP_FLOW } from 'constants/appFlow';

import { getLocaleCode } from 'store/reducers/dealerLocale/selectors';
import {
  actions as personInfoActions,
  selectors as personInfoSelectors
} from 'store/reducers/user/personInfo/';
import { getContactInfo } from 'store/reducers/appointment/selectors';

import { actions as socialActions } from 'store/reducers/user/social/';
import type { Provider } from 'store/reducers/user/social/ISocial';
import * as appointmentActions from 'store/reducers/appointment/actions';
import { setTokenId } from 'store/reducers/tokenId/actions';
import { setAppFlow } from 'store/reducers/userInteractions/actions';

export const SOCIAL_LOGIN_REQUEST = 'user/SOCIAL_LOGIN_REQUEST';
const socialLoginRequest = createAction(SOCIAL_LOGIN_REQUEST);
export const SOCIAL_LOGIN_SUCCESS = 'user/SOCIAL_LOGIN_SUCCESS';
const socialLoginSuccess = createAction(SOCIAL_LOGIN_SUCCESS);
export const SOCIAL_LOGIN_FAILURE = 'user/SOCIAL_LOGIN_FAILURE';
const socialLoginFailure = createAction(SOCIAL_LOGIN_FAILURE);

/**
 * Wraps the social login request with a request/success/failure action types
 */
const fetchSocialLogin = (
  provider: OAuth
): Thunk<Promise<SocialLoginResponse>> => async (dispatch, getState) => {
  // api verification
  invariant(qs.has('webkey'), 'webkey is not set');
  const webkey: string = (qs.getWebkey(): any);
  const personId = getContactInfo(getState()).id || (provider: any).personId;

  try {
    dispatch(socialLoginRequest(provider));
    const ws = await socialLoginWs({
      provider,
      webkey,
      locale: getLocaleCode(getState()),
      channel: CHANNEL,
      socialProviderUserTokenId: await provider.getToken(),
      personId
    });
    const response = ws.data;
    dispatch(socialLoginSuccess(response));
    return response;
  } catch (err) {
    dispatch(socialLoginFailure(err));
    throw err;
  }
};

export type SocialLoginStatus = {
  response: SocialLoginResponse
};

/**
 * Attempts to do the social login auth matching the oauth provider profile
 * with an xtime user
 *
 * Algorithm:
 * - backend attempt to match a social user with an existing user (the UI
 *   only sends the auth_token received from the provider)
 * - backend tells us if there was a match or there wasn't enough data
 *   - if there's not enough data then show a popup for the user to complete
 *     - redirect him to the vehicle selection page, backend will send a new
 *     tokenId that'll be used when making the appointment
 *   the missing fields
 *   - if there's enough data then redirect to the user home page (redirection
 *   is a UI effect, see the component SocialLogin.js)
 */
const doSocialLogin = (
  provider: OAuth
): Thunk<Promise<SocialLoginStatus>> => async (dispatch, getState) => {
  const response = await dispatch(fetchSocialLogin(provider));
  // guaranteed to have a new tokenId
  dispatch(setTokenId(response.authTokenId));
  if (!response.personInfo || Object.keys(response.personInfo).length === 0) {
    throw new ClientError('user match failed', {
      statusCode: 400,
      errorCode: ClientError.errorCodes.USER_MATCH_NOT_FOUND,
      meta: response
    });
  }
  // 2nd part of the algorithm
  const personInfo = response.personInfo;
  dispatch(personInfoActions.setPersonInfo(personInfo));
  // TODO(mauricio): refactor this call and the call in reducers/login/actions
  // to set the contactInfo when a vehicle is selected instead of setting
  // it when the profile page is loaded
  const phoneNumber = personInfoSelectors.getPhoneNumber(getState())('HOME');
  const mobilePhone = personInfoSelectors.getPhoneNumber(getState())('MOBILE');
  dispatch(
    appointmentActions.setContactInfo({
      firstName: personInfo.firstName,
      lastName: personInfo.lastName,
      email: personInfo.contactInfo.emailAddress.email,
      phone: phoneNumber || mobilePhone,
      mobilePhone
    })
  );
  return { response };
};

/**
 * Actions to dispatch post social login (either when there's a match or after
 * the user submitted its info in the additional info popup)
 */
const postSocialLogin = (providerName: Provider): Thunk<void> => (
  dispatch,
  getState
) => {
  dispatch(socialActions.partialSignIn(providerName));
  dispatch(setAppFlow(APP_FLOW.LOG_ME_IN));
};

const actions = {
  doSocialLogin,
  fetchSocialLogin,
  postSocialLogin
};
export default actions;

export type Action = ActionType<$Values<typeof actions>>;



// WEBPACK FOOTER //
// ./src/store/reducers/user/socialLoginActions.js